export type AudienceModeId =
  | 'job_seeker'
  | 'business_explorer'
  | 'recruiter'
  | 'staffing_agency'
  | 'career_coach'
  | 'workforce_organization'
  | 'college_career_center'
  | 'sales_professional'
  | 'business_development'
  | 'agency_freelancer';

export type AudienceMode = AudienceModeId;

export const VALID_AUDIENCE_MODES = new Set<AudienceModeId>([
  "job_seeker",
  "business_explorer",
  "recruiter",
  "staffing_agency",
  "career_coach",
  "workforce_organization",
  "college_career_center",
  "sales_professional",
  "business_development",
  "agency_freelancer"
]);

export function isAudienceMode(value: unknown): value is AudienceModeId {
  return (
    typeof value === "string" &&
    VALID_AUDIENCE_MODES.has(value as AudienceModeId)
  );
}

export interface AudienceModeConfig {
  id: AudienceModeId;
  label: string;
  shortLabel: string;
  landingRoute: string;
  iconName: string;
  tagline: string;
  description: string;
  targetAudience: string;
  heroHeadline: string;
  heroSubheadline: string;
  terminology: {
    opportunityTerm: string;
    signalTerm: string;
    scoreTerm: string;
    badgeLabel: string;
    ctaText: string;
    saveActionText: string;
    pipelineTitle: string;
    listTitle: string;
    searchHeader: string;
  };
  searchPresets: Array<{
    label: string;
    keyword: string;
    description: string;
  }>;
  rankingWeights: {
    hiringSignal: number;
    recency: number;
    distance: number;
    websiteIssue: number;
    seoIssue: number;
    multiLocation: number;
    contactAvailability: number;
  };
  upgradePrompt: {
    title: string;
    description: string;
    bulletPoints: string[];
  };
  pipelineStages: Array<{
    id: string;
    label: string;
    color: string;
  }>;
}

export const AUDIENCE_MODES: Record<AudienceModeId, AudienceModeConfig> = {
  job_seeker: {
    id: 'job_seeker',
    label: 'Job Seeker',
    shortLabel: 'Job Seeker',
    landingRoute: '/for-job-seekers',
    iconName: 'UserCheck',
    tagline: 'Discover local businesses near you that may be hiring before jobs are posted online',
    description: 'Bypass crowded job boards. Connect directly with nearby independent employers, medical practices, trade contractors, and local companies.',
    targetAudience: 'Active and passive local job seekers, career switchers, and trade specialists.',
    heroHeadline: 'Find Nearby Employers and Hiring Opportunities',
    heroSubheadline: 'Explore local businesses, review hiring signals, and find ways to contact employers directly — even when they don’t have a public job listing.',
    terminology: {
      opportunityTerm: 'Job Opportunity',
      signalTerm: 'Hiring Signal',
      scoreTerm: 'Opportunity Score',
      badgeLabel: 'Possible Employer',
      ctaText: 'Contact Employer',
      saveActionText: 'Save Opportunity',
      pipelineTitle: 'Application Pipeline',
      listTitle: 'Saved Target Employers',
      searchHeader: 'Find Nearby Employers & Unposted Opportunities',
    },
    searchPresets: [
      { label: 'Healthcare & Dental', keyword: 'Medical Clinic Dental Practice Care', description: 'Clinics, dental offices, and care facilities' },
      { label: 'Trades & Construction', keyword: 'Contractor Plumbing HVAC Electrical', description: 'Trade shops, HVAC, plumbing, and electric' },
      { label: 'Auto & Repair', keyword: 'Auto Repair Mechanic Body Shop', description: 'Independent auto shops and service centers' },
      { label: 'Retail & Local Services', keyword: 'Boutique Garden Center Veterinary', description: 'Local retail shops and service businesses' },
    ],
    rankingWeights: {
      hiringSignal: 0.45,
      recency: 0.2,
      distance: 0.2,
      websiteIssue: 0.0,
      seoIssue: 0.0,
      multiLocation: 0.05,
      contactAvailability: 0.1,
    },
    upgradePrompt: {
      title: 'Upgrade to Job Seeker Pro',
      description: 'Get unlimited local employer searches, route planning, and instant hiring signal notifications.',
      bulletPoints: [
        'Unlimited local business searches across all zip codes',
        'Multi-location route planner for optimized drop-ins',
        'Direct email & phone contact discovery',
        'Priority hiring-signal alerts for new growth activity',
      ],
    },
    pipelineStages: [
      { id: 'discovered', label: 'Discovered', color: 'bg-slate-100 text-slate-700' },
      { id: 'researching', label: 'Researching', color: 'bg-blue-100 text-blue-700' },
      { id: 'contacted', label: 'Contacted / Applied', color: 'bg-amber-100 text-amber-700' },
      { id: 'interviewing', label: 'Interviewing', color: 'bg-purple-100 text-purple-700' },
      { id: 'offered', label: 'Job Offer', color: 'bg-emerald-100 text-emerald-700' },
    ],
  },

  business_explorer: {
    id: 'business_explorer',
    label: 'Business Explorer',
    shortLabel: 'Business Explorer',
    landingRoute: '/for-business-explorers',
    iconName: 'Compass',
    tagline: 'Discover restaurants, shops, services, and local businesses near you',
    description: 'Discover restaurants, shops, services, healthcare providers, and other businesses near you.',
    targetAudience: 'Consumers, local residents, and visitors looking for places and local services.',
    heroHeadline: 'Discover Local Businesses, Restaurants & Services Near You',
    heroSubheadline: 'Explore top-rated local spots, see opening hours, and get direct directions.',
    terminology: {
      opportunityTerm: 'Local Business',
      signalTerm: 'Popularity Level',
      scoreTerm: 'Best Match',
      badgeLabel: 'Local Business',
      ctaText: 'Call Business',
      saveActionText: 'Save Business',
      pipelineTitle: 'Saved Businesses',
      listTitle: 'Saved Businesses',
      searchHeader: 'Find Nearby Businesses, Restaurants & Services',
    },
    searchPresets: [
      { label: 'Italian restaurants', keyword: 'Italian restaurant bistro', description: 'Italian dining and pasta places' },
      { label: 'Dentists', keyword: 'Dentist dental practice care', description: 'Dental offices and care providers' },
      { label: 'Auto repair', keyword: 'Auto repair mechanic body shop', description: 'Local automotive repair and service' },
      { label: 'Coffee shops', keyword: 'Coffee shop café espresso', description: 'Cafés and morning coffee spots' },
      { label: 'Physical therapy', keyword: 'Physical therapy rehab clinic', description: 'Rehabilitation and therapy providers' },
      { label: 'Pet groomers', keyword: 'Pet groomer veterinary animal care', description: 'Local pet care and grooming services' },
      { label: 'Gaming stores', keyword: 'Gaming store hobby table games', description: 'Board game and electronics shops' }
    ],
    rankingWeights: {
      hiringSignal: 0.0,
      recency: 0.1,
      distance: 0.35,
      websiteIssue: 0.0,
      seoIssue: 0.0,
      multiLocation: 0.15,
      contactAvailability: 0.2,
    },
    upgradePrompt: {
      title: 'Upgrade to Business Explorer Pro',
      description: 'Get unlimited local business searches, saved place lists, and route maps.',
      bulletPoints: [
        'Unlimited local business searches across all zip codes',
        'Multi-stop route planner for local visits',
        'Direct phone & website contact access',
        'Real-time opening status & popularity insights',
      ],
    },
    pipelineStages: [
      { id: 'discovered', label: 'Discovered', color: 'bg-slate-100 text-slate-700' },
      { id: 'want_to_visit', label: 'Want to Visit', color: 'bg-blue-100 text-blue-700' },
      { id: 'visited', label: 'Visited', color: 'bg-emerald-100 text-emerald-700' },
      { id: 'favorite', label: 'Favorite', color: 'bg-purple-100 text-purple-700' }
    ]
  },

  recruiter: {
    id: 'recruiter',
    label: 'Recruiter',
    shortLabel: 'Recruiter',
    landingRoute: '/for-recruiters',
    iconName: 'UserPlus',
    tagline: 'Identify growing businesses with active hiring needs and expansion signals',
    description: 'Track employer headcount growth, new facility openings, and hiring activity to source new client accounts before competitors.',
    targetAudience: 'Third-party recruiters, executive search firms, and talent acquisition specialists.',
    heroHeadline: 'Source Employer Accounts Showing Active Growth & Hiring Demand',
    heroSubheadline: 'Spot expanding local companies, track recruitment signals, and win new recruiting clients.',
    terminology: {
      opportunityTerm: 'Recruiting Prospect',
      signalTerm: 'Hiring Activity Evidence',
      scoreTerm: 'Recruiting Priority Score',
      badgeLabel: 'Hiring Employer Target',
      ctaText: 'Contact Hiring Manager',
      saveActionText: 'Save Recruiting Target',
      pipelineTitle: 'Recruiter Prospect Pipeline',
      listTitle: 'Recruiting Prospect Accounts',
      searchHeader: 'Recruiter Employer Discovery & Activity Search',
    },
    searchPresets: [
      { label: 'Rapidly Growing Medical', keyword: 'Medical Group Urgent Care Health System', description: 'Expanding healthcare groups and clinics' },
      { label: 'Expanding Commercial Contractors', keyword: 'General Contractor Commercial Construction Engineering', description: 'Commercial builders and trade firms' },
      { label: 'Multi-Location Operations', keyword: 'Logistics Warehouse Manufacturing Services', description: 'Multi-site industrial & service businesses' },
      { label: 'Recent Business Openings', keyword: 'New Opening Tech Office Professional Firm', description: 'New commercial locations and branch sites' },
    ],
    rankingWeights: {
      hiringSignal: 0.4,
      recency: 0.25,
      distance: 0.1,
      websiteIssue: 0.05,
      seoIssue: 0.0,
      multiLocation: 0.1,
      contactAvailability: 0.1,
    },
    upgradePrompt: {
      title: 'Upgrade to Recruiter Pro',
      description: 'Unlock multi-location employer search, automated hiring-signal alerts, and unlimited recruiter list exports.',
      bulletPoints: [
        'Multi-location search across cities and metro areas simultaneously',
        'Automated hiring-activity monitoring and weekly client alerts',
        'Direct hiring-manager and owner phone/email lookup',
        'CSV list export for ATS/CRM lead import',
      ],
    },
    pipelineStages: [
      { id: 'discovered', label: 'Discovered Account', color: 'bg-slate-100 text-slate-700' },
      { id: 'qualified', label: 'Qualified Need', color: 'bg-blue-100 text-blue-700' },
      { id: 'outreach', label: 'Outreach Initiated', color: 'bg-amber-100 text-amber-700' },
      { id: 'meeting', label: 'Client Meeting Set', color: 'bg-purple-100 text-purple-700' },
      { id: 'signed_client', label: 'Active Fee Agreement', color: 'bg-emerald-100 text-emerald-700' },
    ],
  },

  staffing_agency: {
    id: 'staffing_agency',
    label: 'Staffing Agency',
    shortLabel: 'Staffing',
    landingRoute: '/for-staffing-agencies',
    iconName: 'Building2',
    tagline: 'Target employers requiring temporary, seasonal, and high-volume staffing support',
    description: 'Locate local employers facing seasonal demand surges, high turnover, or multi-shift operational staffing gaps.',
    targetAudience: 'Commercial staffing agencies, light industrial, healthcare, and seasonal labor agencies.',
    heroHeadline: 'Find Local Employers Needing Temporary & High-Volume Staffing',
    heroSubheadline: 'Pinpoint companies with high turnover, seasonal hiring spikes, or multi-shift staffing needs.',
    terminology: {
      opportunityTerm: 'Staffing Prospect',
      signalTerm: 'Staffing Need Indicator',
      scoreTerm: 'Staffing Fit Score',
      badgeLabel: 'High-Volume Employer',
      ctaText: 'Contact Staffing Decision Maker',
      saveActionText: 'Save Staffing Prospect',
      pipelineTitle: 'Staffing Sales Pipeline',
      listTitle: 'Staffing Client Prospects',
      searchHeader: 'Staffing Lead Discovery & Seasonal Demand Search',
    },
    searchPresets: [
      { label: 'Warehouse & Logistics', keyword: 'Distribution Logistics Shipping Freight Warehouse', description: 'Logistics hubs and distribution centers' },
      { label: 'Manufacturing & Light Industrial', keyword: 'Manufacturing Plant Assembly Packaging', description: 'Factories, assembly plants, and processors' },
      { label: 'Hospitality & Events', keyword: 'Catering Banquet Hotel Event Staff', description: 'Hotels, venues, and hospitality groups' },
      { label: 'Construction & Field Labor', keyword: 'Demolition Excavation Landscaping Roofing', description: 'High-turnover field crews and trade contractors' },
    ],
    rankingWeights: {
      hiringSignal: 0.35,
      recency: 0.2,
      distance: 0.1,
      websiteIssue: 0.05,
      seoIssue: 0.0,
      multiLocation: 0.15,
      contactAvailability: 0.15,
    },
    upgradePrompt: {
      title: 'Upgrade to Staffing Pro',
      description: 'Streamline client acquisition with multi-location prospecting, pipeline workflows, and team list exports.',
      bulletPoints: [
        'Simultaneous multi-city & industrial park territory prospecting',
        'Seasonal demand spike monitoring and instant alerts',
        'Decision-maker contact details (owners, GM, HR managers)',
        'Bulk CSV export and team territory assignment',
      ],
    },
    pipelineStages: [
      { id: 'discovered', label: 'Discovered Lead', color: 'bg-slate-100 text-slate-700' },
      { id: 'contacted', label: 'Pitch Sent', color: 'bg-blue-100 text-blue-700' },
      { id: 'meeting', label: 'Discovery Call', color: 'bg-amber-100 text-amber-700' },
      { id: 'proposal', label: 'Proposal Sent', color: 'bg-purple-100 text-purple-700' },
      { id: 'contracted', label: 'Active Staffing Client', color: 'bg-emerald-100 text-emerald-700' },
    ],
  },

  career_coach: {
    id: 'career_coach',
    label: 'Career Coach',
    shortLabel: 'Career Coach',
    landingRoute: '/for-career-coaches',
    iconName: 'GraduationCap',
    tagline: 'Research, track, and curate targeted local job opportunities for your coaching clients',
    description: 'Manage distinct client workspaces, curate custom employer target lists, and guide clients toward high-probability local openings.',
    targetAudience: 'Independent career coaches, outplacement consultants, and career strategists.',
    heroHeadline: 'Curate Target Employer Lists & Unposted Job Opportunities for Clients',
    heroSubheadline: 'Organize client-specific job search workspaces, research local employers, and track application progress.',
    terminology: {
      opportunityTerm: 'Client Opportunity Match',
      signalTerm: 'Employer Hiring Signal',
      scoreTerm: 'Client Fit Score',
      badgeLabel: 'Recommended Employer',
      ctaText: 'Assign to Client',
      saveActionText: 'Save for Client',
      pipelineTitle: 'Client Job Search Tracker',
      listTitle: 'Client Target Employers',
      searchHeader: 'Career Coach Employer Research & Client Opportunity Hub',
    },
    searchPresets: [
      { label: 'Mid-Career Professional Firms', keyword: 'Accounting Wealth Management Law Firm Consulting', description: 'Professional services and boutique firms' },
      { label: 'Healthcare & Wellness', keyword: 'Physical Therapy Dental Practice Clinic Rehabilitation', description: 'Local healthcare providers and practices' },
      { label: 'Local Tech & Marketing Agencies', keyword: 'Digital Marketing Software IT Services Media', description: 'Tech agencies and boutique consulting' },
      { label: 'Nonprofit & Education', keyword: 'Nonprofit Foundation Community School Training', description: 'Community orgs and local education' },
    ],
    rankingWeights: {
      hiringSignal: 0.35,
      recency: 0.25,
      distance: 0.15,
      websiteIssue: 0.0,
      seoIssue: 0.0,
      multiLocation: 0.05,
      contactAvailability: 0.2,
    },
    upgradePrompt: {
      title: 'Upgrade to Coach Pro',
      description: 'Unlock unlimited client workspaces, branded client export reports, and direct contact details.',
      bulletPoints: [
        'Unlimited separate client workspaces and target lists',
        'Export PDF & CSV opportunity summaries with client notes',
        'Direct hiring manager and owner contact discovery',
        'Automated opportunity alerts tailored per client profile',
      ],
    },
    pipelineStages: [
      { id: 'recommended', label: 'Recommended to Client', color: 'bg-slate-100 text-slate-700' },
      { id: 'client_approved', label: 'Client Approved', color: 'bg-blue-100 text-blue-700' },
      { id: 'outreach_sent', label: 'Outreach / Application Sent', color: 'bg-amber-100 text-amber-700' },
      { id: 'interview', label: 'Interview Scheduled', color: 'bg-purple-100 text-purple-700' },
      { id: 'hired', label: 'Client Placed / Hired', color: 'bg-emerald-100 text-emerald-700' },
    ],
  },

  workforce_organization: {
    id: 'workforce_organization',
    label: 'Workforce Organization',
    shortLabel: 'Workforce Org',
    landingRoute: '/for-workforce-organizations',
    iconName: 'Globe',
    tagline: 'Track regional employer hiring activity and map job opportunities across community districts',
    description: 'Empower workforce development boards, municipal employment agencies, and non-profits with real-time local business signals.',
    targetAudience: 'Workforce Development Boards, One-Stop Career Centers, community non-profits, and regional economic groups.',
    heroHeadline: 'Map Regional Employer Hiring Signals & Drive Community Job Placement',
    heroSubheadline: 'Gain visibility into local small business growth, track regional employer demand, and support program participants.',
    terminology: {
      opportunityTerm: 'Regional Employer Opportunity',
      signalTerm: 'Local Hiring Activity',
      scoreTerm: 'Regional Employer Impact Score',
      badgeLabel: 'Community Employer',
      ctaText: 'Engage Employer',
      saveActionText: 'Save Regional Employer',
      pipelineTitle: 'Employer Engagement Pipeline',
      listTitle: 'Regional Employer Network',
      searchHeader: 'Workforce Development Employer Intelligence & Regional Search',
    },
    searchPresets: [
      { label: 'Trade & Apprentice Employers', keyword: 'HVAC Electrical Contractor Fabrication Repair', description: 'Apprenticeships and trade workplaces' },
      { label: 'Essential Community Employers', keyword: 'Grocery Care Center Transport Facility Services', description: 'Community essential services and operators' },
      { label: 'Regional Industrial Employers', keyword: 'Warehouse Light Assembly Machine Shop Packaging', description: 'Regional manufacturing and logistics' },
      { label: 'Entry-Level Retail & Hospitality', keyword: 'Retail Restaurant Food Service Hospitality', description: 'Entry-level customer service and food service' },
    ],
    rankingWeights: {
      hiringSignal: 0.35,
      recency: 0.2,
      distance: 0.2,
      websiteIssue: 0.0,
      seoIssue: 0.0,
      multiLocation: 0.1,
      contactAvailability: 0.15,
    },
    upgradePrompt: {
      title: 'Upgrade to Workforce Pro',
      description: 'Equip your agency with regional multi-zip mapping, participant exports, and employer engagement reporting.',
      bulletPoints: [
        'Multi-district and county-wide employer search filtering',
        'Shareable participant opportunity summaries and job leads',
        'Employer outreach & partnership tracking CRM',
        'Regional economic hiring-signal report generation',
      ],
    },
    pipelineStages: [
      { id: 'identified', label: 'Identified Employer', color: 'bg-slate-100 text-slate-700' },
      { id: 'outreach', label: 'Initial Outreach', color: 'bg-blue-100 text-blue-700' },
      { id: 'engaged', label: 'Employer Engaged', color: 'bg-amber-100 text-amber-700' },
      { id: 'participant_referred', label: 'Participants Referred', color: 'bg-purple-100 text-purple-700' },
      { id: 'placed', label: 'Community Placements Made', color: 'bg-emerald-100 text-emerald-700' },
    ],
  },

  college_career_center: {
    id: 'college_career_center',
    label: 'College Career Center',
    shortLabel: 'Career Center',
    landingRoute: '/for-college-career-centers',
    iconName: 'BookOpen',
    tagline: 'Connect students and recent grads with local employers, entry-level jobs, and internships',
    description: 'Help students discover nearby small businesses, clinics, trade shops, and agencies willing to hire interns and entry-level grads.',
    targetAudience: 'University career centers, community college career offices, vocational schools, and alumni networks.',
    heroHeadline: 'Find Nearby Employers Offering Student Internships & Entry-Level Roles',
    heroSubheadline: 'Connect students with local business owners, map campus-adjacent employers, and build employer partnerships.',
    terminology: {
      opportunityTerm: 'Student Opportunity Target',
      signalTerm: 'Entry-Level / Internship Signal',
      scoreTerm: 'Student Match Score',
      badgeLabel: 'Entry-Level Employer',
      ctaText: 'Contact for Partnership',
      saveActionText: 'Save Employer Partner',
      pipelineTitle: 'Employer Partner Pipeline',
      listTitle: 'Student Employer Directory',
      searchHeader: 'College Career Center Local Employer & Internship Search',
    },
    searchPresets: [
      { label: 'Clinic & Healthcare Internships', keyword: 'Clinic Physical Therapy Dental Lab Practice', description: 'Healthcare & pre-med student opportunities' },
      { label: 'Marketing & Tech Agencies', keyword: 'Digital Agency Software Design Marketing IT Services', description: 'Tech, design, and media agencies' },
      { label: 'Finance & Accounting Firms', keyword: 'CPA Accounting Tax Wealth Management Real Estate', description: 'Business & finance student employers' },
      { label: 'Trades & Vocational Workplaces', keyword: 'Auto Shop Electrical HVAC Fabrication Machine Shop', description: 'Technical & trade apprenticeship sites' },
    ],
    rankingWeights: {
      hiringSignal: 0.4,
      recency: 0.2,
      distance: 0.25,
      websiteIssue: 0.0,
      seoIssue: 0.0,
      multiLocation: 0.0,
      contactAvailability: 0.15,
    },
    upgradePrompt: {
      title: 'Upgrade to Career Center Pro',
      description: 'Manage employer relationships across academic departments, export student opportunity guides, and track campus outreach.',
      bulletPoints: [
        'Multi-department employer list segmentation and student exports',
        'Campus commute-radius location filtering and route mapping',
        'Direct contact details for business owners and HR lead contacts',
        'Employer partner CRM and career fair outreach tracking',
      ],
    },
    pipelineStages: [
      { id: 'discovered', label: 'Discovered Employer', color: 'bg-slate-100 text-slate-700' },
      { id: 'contacted', label: 'Outreach Sent', color: 'bg-blue-100 text-blue-700' },
      { id: 'partner', label: 'Official Employer Partner', color: 'bg-purple-100 text-purple-700' },
      { id: 'active_jobs', label: 'Active Interns / Grads Hired', color: 'bg-emerald-100 text-emerald-700' },
    ],
  },

  sales_professional: {
    id: 'sales_professional',
    label: 'Sales Professional',
    shortLabel: 'Sales Pro',
    landingRoute: '/for-sales-teams',
    iconName: 'TrendingUp',
    tagline: 'Find local commercial businesses that need B2B services, technology, or consulting',
    description: 'Filter businesses by website status, SEO presence, tech stack, and growth indicators to generate warm B2B sales leads.',
    targetAudience: 'B2B sales reps, account executives, commercial account managers, and field sales teams.',
    heroHeadline: 'Discover Qualified Local B2B Sales Prospects & Service Opportunities',
    heroSubheadline: 'Spot growing businesses, detect tech gaps and website issues, and reach key decision-makers.',
    terminology: {
      opportunityTerm: 'Sales Lead Prospect',
      signalTerm: 'Service Opportunity Signal',
      scoreTerm: 'Prospect Fit Score',
      badgeLabel: 'B2B Sales Target',
      ctaText: 'Pitch Business',
      saveActionText: 'Save Sales Target',
      pipelineTitle: 'B2B Sales Pipeline',
      listTitle: 'Qualified Sales Prospects',
      searchHeader: 'B2B Sales Prospecting & Business Opportunity Engine',
    },
    searchPresets: [
      { label: 'High-Growth Trade Contractors', keyword: 'HVAC Plumbing Commercial Electric General Contractor', description: 'Growing trade & commercial service companies' },
      { label: 'Independent Medical Practices', keyword: 'Dental Clinic Medical Practice Orthodontics Care', description: 'Healthcare practices needing software & services' },
      { label: 'Local Auto & Fleet Operators', keyword: 'Auto Repair Fleet Towing Collision Repair', description: 'Auto repair, fleet, and transport businesses' },
      { label: 'Boutique Professional Services', keyword: 'Law Firm Accounting Wealth Management Consulting', description: 'Professional firms seeking digital tools' },
    ],
    rankingWeights: {
      hiringSignal: 0.15,
      recency: 0.15,
      distance: 0.15,
      websiteIssue: 0.25,
      seoIssue: 0.15,
      multiLocation: 0.05,
      contactAvailability: 0.1,
    },
    upgradePrompt: {
      title: 'Upgrade to Sales Pro',
      description: 'Accelerate field and inside B2B sales with multi-location prospecting, pipeline management, and lead exports.',
      bulletPoints: [
        'Multi-city and zip code batch sales prospecting',
        'Website quality, SSL, and mobile responsive gap analysis',
        'Direct owner & manager phone and email contact lookup',
        'CSV export to Salesforce, HubSpot, or custom CRM',
      ],
    },
    pipelineStages: [
      { id: 'lead', label: 'New Prospect', color: 'bg-slate-100 text-slate-700' },
      { id: 'contacted', label: 'Outreach Initiated', color: 'bg-blue-100 text-blue-700' },
      { id: 'demo', label: 'Discovery / Pitch Call', color: 'bg-amber-100 text-amber-700' },
      { id: 'proposal', label: 'Proposal Submitted', color: 'bg-purple-100 text-purple-700' },
      { id: 'won', label: 'Closed Deal', color: 'bg-emerald-100 text-emerald-700' },
    ],
  },

  business_development: {
    id: 'business_development',
    label: 'Business Development',
    shortLabel: 'Business Dev',
    landingRoute: '/for-business-development',
    iconName: 'Briefcase',
    tagline: 'Identify strategic partnerships, cross-referral opportunities, and market expansion targets',
    description: 'Locate multi-location regional players, complementary service providers, and growing businesses for strategic joint ventures.',
    targetAudience: 'Business development directors, partnership managers, strategic growth leaders, and franchise developers.',
    heroHeadline: 'Find Local Strategic Partners & Market Expansion Opportunities',
    heroSubheadline: 'Track growing multi-location companies, identify referral partners, and expand regional partnerships.',
    terminology: {
      opportunityTerm: 'Partnership Prospect',
      signalTerm: 'Business Expansion Signal',
      scoreTerm: 'Partnership Fit Score',
      badgeLabel: 'Strategic Partner',
      ctaText: 'Initiate BD Outreach',
      saveActionText: 'Save Partnership Target',
      pipelineTitle: 'Business Development Pipeline',
      listTitle: 'Strategic Partnership Targets',
      searchHeader: 'Business Development & Partnership Discovery Engine',
    },
    searchPresets: [
      { label: 'Multi-Location Healthcare', keyword: 'Health System Urgent Care Group Dental Network', description: 'Regional healthcare groups and networks' },
      { label: 'Commercial Real Estate & Property', keyword: 'Property Management Commercial Realty Maintenance', description: 'Property managers and real estate networks' },
      { label: 'Regional Industrial Services', keyword: 'Equipment Rental Industrial Supply Freight Logistics', description: 'Industrial suppliers and logistics providers' },
      { label: 'Financial & Legal Partners', keyword: 'Wealth Management Commercial Bank Law Group CPA', description: 'Financial advisors, CPAs, and law firms' },
    ],
    rankingWeights: {
      hiringSignal: 0.2,
      recency: 0.15,
      distance: 0.15,
      websiteIssue: 0.05,
      seoIssue: 0.05,
      multiLocation: 0.25,
      contactAvailability: 0.15,
    },
    upgradePrompt: {
      title: 'Upgrade to Business Dev Pro',
      description: 'Unlock multi-market expansion prospecting, executive contact lookup, and partnership list management.',
      bulletPoints: [
        'Multi-state and regional territory expansion search',
        'Multi-location company footprint detection',
        'Executive & managing partner contact details',
        'Export partnership target briefs and CSV reports',
      ],
    },
    pipelineStages: [
      { id: 'identified', label: 'Target Identified', color: 'bg-slate-100 text-slate-700' },
      { id: 'initial_contact', label: 'Initial Contact', color: 'bg-blue-100 text-blue-700' },
      { id: 'discussion', label: 'Partnership Discussion', color: 'bg-amber-100 text-amber-700' },
      { id: 'term_sheet', label: 'MOU / Term Sheet', color: 'bg-purple-100 text-purple-700' },
      { id: 'partnered', label: 'Active Partner', color: 'bg-emerald-100 text-emerald-700' },
    ],
  },

  agency_freelancer: {
    id: 'agency_freelancer',
    label: 'Agency & Freelancer',
    shortLabel: 'Agency / Freelancer',
    landingRoute: '/for-agencies',
    iconName: 'Zap',
    tagline: 'Find local businesses that need website design, SEO, digital marketing, or IT support',
    description: 'Pinpoint local businesses with missing websites, outdated designs, poor SEO rankings, or missing contact channels to pitch client work.',
    targetAudience: 'Web design agencies, SEO consultants, freelance developers, digital marketers, and IT service providers.',
    heroHeadline: 'Find Local Businesses Needing Web Design, SEO, & Marketing Services',
    heroSubheadline: 'Scan local businesses for missing websites, SEO flaws, and technology gaps to win agency clients.',
    terminology: {
      opportunityTerm: 'Client Service Opportunity',
      signalTerm: 'Website/SEO Opportunity Signal',
      scoreTerm: 'Client Prospect Score',
      badgeLabel: 'Agency Prospect',
      ctaText: 'Send Proposal / Pitch',
      saveActionText: 'Save Agency Prospect',
      pipelineTitle: 'Agency Pitch Pipeline',
      listTitle: 'Agency Target Clients',
      searchHeader: 'Agency & Freelance Web/SEO Prospecting Engine',
    },
    searchPresets: [
      { label: 'Businesses Without Websites', keyword: 'Contractor Auto Repair Plumbing HVAC Landscaping', description: 'Local companies operating without a website' },
      { label: 'Local Medical & Dental', keyword: 'Dental Practice Clinic Medical Chiropractic', description: 'Healthcare practices needing modern websites & SEO' },
      { label: 'Trade Services & Home Repair', keyword: 'Electrician Roofing HVAC Plumbing Painting', description: 'Trade businesses needing local SEO & leads' },
      { label: 'Boutique Restaurants & Shops', keyword: 'Restaurant Boutique Bakery Cafe Salon', description: 'Local retail & dining needing mobile/web refresh' },
    ],
    rankingWeights: {
      hiringSignal: 0.1,
      recency: 0.1,
      distance: 0.1,
      websiteIssue: 0.35,
      seoIssue: 0.25,
      multiLocation: 0.0,
      contactAvailability: 0.1,
    },
    upgradePrompt: {
      title: 'Upgrade to Agency Pro',
      description: 'Supercharge your client acquisition with full website audits, SEO gap detection, and lead exports.',
      bulletPoints: [
        'Automated website review & mobile responsiveness audit',
        'SEO metadata, schema, and Google Maps ranking gap analysis',
        'Direct business owner phone and email lookup',
        'CSV list export for outbound email & field sales campaigns',
      ],
    },
    pipelineStages: [
      { id: 'prospect', label: 'New Prospect', color: 'bg-slate-100 text-slate-700' },
      { id: 'audited', label: 'Website Audited', color: 'bg-blue-100 text-blue-700' },
      { id: 'pitched', label: 'Proposal Sent', color: 'bg-amber-100 text-amber-700' },
      { id: 'negotiating', label: 'Negotiating Terms', color: 'bg-purple-100 text-purple-700' },
      { id: 'closed_client', label: 'Retained Client', color: 'bg-emerald-100 text-emerald-700' },
    ],
  },
};

export interface AudienceModeDetails {
  title: string;
  description: string;
  changes: string[];
}

export const AUDIENCE_MODE_DETAILS: Record<AudienceModeId, AudienceModeDetails> = {
  job_seeker: {
    title: "How Job Seeker mode works",
    description: "Find nearby businesses that may be hiring, compare opportunities, review transparent hiring signals, save possible employers, and organize applications or outreach.",
    changes: [
      "Best Match prioritizes hiring evidence, distance, job relevance, and travel practicality.",
      "Opportunity Score estimates how useful a business may be for your job search.",
      "Results use job-seeker terms such as Hiring Signal, Possible Employer, and Job Opportunity.",
      "Recommended tools focus on saving businesses, contacting employers, planning visits, and tracking applications."
    ]
  },
  business_explorer: {
    title: "How Business Explorer mode works",
    description: "Discover restaurants, shops, services, healthcare providers, and other local businesses near you.",
    changes: [
      "Best Match treats brand popularity and recognition as a positive signal alongside category relevance, distance, and reviews.",
      "Results highlight popularity levels, opening status, ratings, and available services.",
      "Popularity metrics do not penalize established or famous local businesses.",
      "Recommended tools focus on exploring places, viewing services, getting directions, calling businesses, and saving places to visit."
    ]
  },
  recruiter: {
    title: "How Recruiter mode works",
    description: "Find businesses showing hiring activity, compare potential employer clients, review hiring evidence, and organize recruiting outreach.",
    changes: [
      "Best Match prioritizes recent hiring activity, repeated job postings, business growth, and contact availability.",
      "Opportunity Score becomes a recruiting-prospect priority score.",
      "Results explain why a business may be a relevant recruiting prospect.",
      "Recommended tools focus on employer lists, outreach tracking, comparisons, notes, follow-ups, and hiring-signal monitoring."
    ]
  },
  staffing_agency: {
    title: "How Staffing Agency mode works",
    description: "Identify businesses that may have recurring, seasonal, temporary, or high-volume staffing needs.",
    changes: [
      "Best Match prioritizes repeated hiring, multiple openings, seasonal activity, multiple locations, and potential staffing demand.",
      "Opportunity Score becomes a staffing-prospect priority score.",
      "Results use staffing terminology without claiming a business has agreed to use an agency.",
      "Recommended tools focus on staffing prospect lists, pipelines, contact research, follow-ups, and monitoring."
    ]
  },
  career_coach: {
    title: "How Career Coach mode works",
    description: "Organize employer opportunities for multiple clients and track each client’s job-search progress.",
    changes: [
      "Best Match can prioritize client preferences, distance, job category, and hiring evidence.",
      "Opportunity Score becomes a client-opportunity match.",
      "Results focus on employer relevance for the active client.",
      "Recommended tools focus on client workspaces, saved opportunities, notes, application tracking, and reports."
    ]
  },
  workforce_organization: {
    title: "How Workforce Organization mode works",
    description: "Research local employer activity, organize business outreach, and discover opportunities for workforce-program participants.",
    changes: [
      "Best Match prioritizes regional relevance, employer activity, industries, and available opportunities.",
      "Opportunity Score becomes a regional employer-opportunity score.",
      "Results focus on local employer engagement and participant opportunities.",
      "Recommended tools focus on regional searches, employer lists, program notes, maps, outreach tracking, and reports."
    ]
  },
  college_career_center: {
    title: "How College Career Center mode works",
    description: "Find local employers, internships, apprenticeships, and entry-level opportunities that may be relevant to students.",
    changes: [
      "Best Match prioritizes program relevance, entry-level indicators, distance, internships, and employer activity.",
      "Opportunity Score becomes a student-employer relevance score.",
      "Results focus on student opportunities and potential employer outreach.",
      "Recommended tools focus on program-specific employer lists, partnership tracking, career-fair research, and exports."
    ]
  },
  sales_professional: {
    title: "How Sales Professional mode works",
    description: "Find local businesses that may benefit from relevant products or services using public business, website, growth, and contact signals.",
    changes: [
      "Best Match prioritizes service fit, website findings, contact availability, business category, and growth indicators.",
      "Opportunity Score becomes a prospect-fit score.",
      "Results use terms such as Prospect, Service Opportunity, and Outreach Reason.",
      "Recommended tools focus on prospect lists, website reviews, SEO audits, contact research, pipelines, and follow-ups."
    ]
  },
  business_development: {
    title: "How Business Development mode works",
    description: "Discover potential partnerships, growing businesses, expansion opportunities, and local organizations worth contacting.",
    changes: [
      "Best Match prioritizes partnership fit, expansion indicators, multiple locations, industry relevance, and contact availability.",
      "Opportunity Score becomes a partnership-opportunity score.",
      "Results focus on potential relationships and business-development opportunities.",
      "Recommended tools focus on partnership lists, comparison, notes, outreach, follow-ups, and regional research."
    ]
  },
  agency_freelancer: {
    title: "How Agency or Freelancer mode works",
    description: "Find businesses that may benefit from websites, SEO, marketing, branding, development, photography, consulting, or other professional services.",
    changes: [
      "Best Match prioritizes service fit, website issues, SEO findings, technology signals, contact availability, and location.",
      "Opportunity Score becomes a potential-client fit score.",
      "Results explain specific improvement indicators without insulting the business or claiming it wants to buy.",
      "Recommended tools focus on website audits, SEO audits, technology detection, prospect lists, pipelines, route planning, and outreach."
    ]
  }
};

export const DEFAULT_AUDIENCE_MODE: AudienceModeId = 'job_seeker';

export function normalizeAudienceMode(value: unknown): AudienceModeId {
  const aliases: Record<string, AudienceModeId> = {
    "job-seeker": "job_seeker",
    "Job Seeker": "job_seeker",

    "staffing-agency": "staffing_agency",
    "Staffing Agency": "staffing_agency",

    "career-coach": "career_coach",
    "Career Coach": "career_coach",

    "workforce-organization": "workforce_organization",
    "Workforce Organization": "workforce_organization",
    "workforce_org": "workforce_organization",

    "college-career-center": "college_career_center",
    "College Career Center": "college_career_center",
    "career_center": "college_career_center",

    "sales-professional": "sales_professional",
    "Sales Professional": "sales_professional",
    "sales": "sales_professional",
    "sales_pro": "sales_professional",

    "business-development": "business_development",
    "Business Development": "business_development",
    "business_dev": "business_development",

    "agency-freelancer": "agency_freelancer",
    "Agency or Freelancer": "agency_freelancer",
    "agency": "agency_freelancer",
    "agency_freelance": "agency_freelancer",

    "business-explorer": "business_explorer",
    "Business Explorer": "business_explorer",
    "business_explorer": "business_explorer",
    "explorer": "business_explorer",

    "Recruiter": "recruiter"
  };

  if (isAudienceMode(value)) {
    return value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (aliases[trimmed]) {
      return aliases[trimmed];
    }
  }

  return "job_seeker";
}

export const normalizeAudienceModeId = normalizeAudienceMode;

export function getAudienceModeConfig(modeId?: string | null): AudienceModeConfig {
  const normalized = normalizeAudienceMode(modeId);
  return AUDIENCE_MODES[normalized] || AUDIENCE_MODES[DEFAULT_AUDIENCE_MODE];
}

export function getAudienceRankingFactors(mode: AudienceModeId): string[] {
  switch (mode) {
    case "job_seeker":
      return [
        "Direct hiring status & verified open roles (+35 pts)",
        "High hiring likelihood score (+25 pts)",
        "Short commute distance & transit access (+25 pts)",
        "Easy application link & contact access (+10 pts)"
      ];
    case "business_explorer":
      return [
        "Category relevance & keyword match (+35 pts)",
        "Nearby location & proximity (+25 pts)",
        "Open now & operating hours status (+15 pts)",
        "High customer ratings & review quality (+15 pts)",
        "Brand popularity & recognition level (+10 pts)"
      ];
    case "recruiter":
      return [
        "Confirmed recent hiring activity (+35 pts)",
        "Multiple open vacancy indicators (+20 pts)",
        "Multi-unit & expanding business (+15 pts)",
        "Verified decision-maker contact info (+15 pts)"
      ];
    case "staffing_agency":
      return [
        "High-volume openings & shift labor demand (+35 pts)",
        "Turnover-intensive industry profile (+20 pts)",
        "Multi-unit footprint (+15 pts)",
        "Direct telephone line verified (+10 pts)"
      ];
    case "career_coach":
      return [
        "High workplace reputation & review rating (+30 pts)",
        "Confirmed active hiring openings (+25 pts)",
        "Client profile & category alignment (+20 pts)",
        "Accessible location & meeting proximity (+15 pts)"
      ];
    case "workforce_organization":
      return [
        "Regional employer hiring activity (+30 pts)",
        "Established local community presence (+25 pts)",
        "Program participant role suitability (+20 pts)",
        "Verified organizational outreach path (+15 pts)"
      ];
    case "college_career_center":
      return [
        "Entry-level & internship suitability (+35 pts)",
        "Active student hiring signals (+25 pts)",
        "Campus commute proximity (<8 mi) (+20 pts)",
        "Employer partnership fair readiness (+10 pts)"
      ];
    case "sales_professional":
      return [
        "Missing or broken official website (+35 pts)",
        "Verified decision-maker phone/email (+25 pts)",
        "Digital presence & SEO audit findings (+20 pts)",
        "High local customer demand & budget (+15 pts)"
      ];
    case "business_development":
      return [
        "Multi-location brand expansion (+35 pts)",
        "High local market reputation (★4.3+) (+25 pts)",
        "Executive/Manager direct contact (+20 pts)",
        "Metropolitan regional target alignment (+10 pts)"
      ];
    case "agency_freelancer":
      return [
        "Missing or broken website lead (+35 pts)",
        "SSL security & mobile responsiveness needs (+25 pts)",
        "High rating + weak digital footprint sweet spot (+20 pts)",
        "Direct outreach phone line verified (+10 pts)"
      ];
    default:
      return [
        "Direct hiring evidence & active signals",
        "Search relevance & location proximity",
        "Business reputation & contact availability"
      ];
  }
}
